import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Logo } from '../components/Logo';
import { Alert, Badge, Button, Card, Field, Input } from '../components/ui';
import { api } from '../lib/api';

interface SetupStatus {
  hostname: string | null;
  publicIp: string | null;
  dns: {
    resolves: boolean;
    resolvedTo: string[];
    matches: boolean;
    looksProxied: boolean;
  } | null;
  hostnameLocked: boolean;
  certificateReady: boolean;
}

/**
 * First-run hostname setup.
 *
 * Deliberately after account creation and before anything else: the hostname becomes the SIP
 * realm, and every extension credential is derived from it, so it has to be settled while there
 * is still nothing to invalidate.
 *
 * The DNS record is the one step that cannot be automated from inside the server, so the job here
 * is to make it unmissable — show the exact record, check it on demand, and name the specific
 * reasons it usually fails.
 */
export function Setup({ onComplete }: { onComplete: () => void }) {
  const queryClient = useQueryClient();
  const [hostname, setHostname] = useState('');

  const { data: status, isLoading } = useQuery({
    queryKey: ['setup-status'],
    queryFn: () => api.get<SetupStatus>('/api/v1/setup/status'),
  });

  const save = useMutation({
    mutationFn: () => api.post<SetupStatus>('/api/v1/setup/hostname', { hostname }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['setup-status'] }),
  });

  const verify = useMutation({
    mutationFn: () => api.post<SetupStatus>('/api/v1/setup/verify-dns'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['setup-status'] }),
  });

  if (isLoading || !status) return null;

  // Step 1: no hostname yet.
  if (!status.hostname) {
    return (
      <Shell step={1}>
        <h1 className="text-lg font-semibold">What address will this PBX use?</h1>
        <p className="text-sm text-muted-foreground">
          Phones, browsers and your certificate all use this name. Pick one you intend to keep — it
          becomes part of every extension&rsquo;s credentials, so it cannot be changed once you
          start creating extensions.
        </p>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Hostname" hint="A subdomain of a domain you control">
            <Input
              value={hostname}
              onChange={(e) => setHostname(e.target.value.trim().toLowerCase())}
              placeholder="pbx.yourcompany.com"
              autoFocus
              required
            />
          </Field>

          {save.error ? <Alert>{(save.error as Error).message}</Alert> : null}

          <Button type="submit" disabled={save.isPending || hostname.length < 4}>
            {save.isPending ? 'Saving…' : 'Continue'}
          </Button>
        </form>
      </Shell>
    );
  }

  // Step 2: hostname set, DNS not yet correct.
  if (!status.dns?.matches) {
    return (
      <Shell step={2}>
        <h1 className="text-lg font-semibold">Point {status.hostname} at this server</h1>
        <p className="text-sm text-muted-foreground">
          Add this record with your DNS provider. Everything else is already configured.
        </p>

        <div className="overflow-x-auto rounded-[--radius] border border-border">
          <table className="w-full text-sm">
            <tbody>
              <Row label="Type" value="A" />
              <Row label="Name" value={status.hostname.split('.')[0] ?? '@'} />
              <Row label="Value" value={status.publicIp ?? 'detecting…'} />
              <Row label="TTL" value="300 (5 minutes)" />
              <Row label="Proxy" value="OFF — DNS only" />
            </tbody>
          </table>
        </div>

        {/*
          Named explicitly because it is the most common way a correctly-created record still
          fails. A proxied record resolves to the proxy, which terminates TLS, cannot carry the
          UDP that voice needs, and drops the SIP WebSocket subprotocol.
        */}
        {status.dns?.looksProxied ? (
          <Alert>
            <strong>This record is proxied.</strong> It currently resolves to{' '}
            {status.dns.resolvedTo.join(', ')}, which belongs to a CDN rather than this server. In
            Cloudflare, click the orange cloud so it turns grey (&ldquo;DNS only&rdquo;). A proxied
            record cannot carry voice traffic.
          </Alert>
        ) : status.dns?.resolves ? (
          <Alert tone="warning">
            {status.hostname} currently resolves to {status.dns.resolvedTo.join(', ')}, but this
            server is {status.publicIp}. Update the record to match.
          </Alert>
        ) : (
          <Alert tone="warning">
            {status.hostname} does not resolve yet. DNS changes usually take a few minutes.
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={() => verify.mutate()} disabled={verify.isPending}>
            {verify.isPending ? 'Checking…' : 'Check now'}
          </Button>
          <span className="text-sm text-muted-foreground">
            Your certificate is issued automatically once this resolves.
          </span>
        </div>

        <details className="text-sm text-muted-foreground">
          <summary className="cursor-pointer">Continue without DNS</summary>
          <p className="mt-2">
            You can explore the admin interface now, but phones and browsers cannot register until
            the certificate exists. Nothing you configure will be lost.
          </p>
          <Button variant="secondary" className="mt-2" onClick={onComplete}>
            Skip for now
          </Button>
        </details>
      </Shell>
    );
  }

  // Step 3: DNS is correct.
  return (
    <Shell step={3}>
      <h1 className="text-lg font-semibold">Ready</h1>
      <div className="space-y-2 text-sm">
        <p className="flex items-center gap-2">
          <Badge tone="success">DNS</Badge>
          {status.hostname} → {status.publicIp}
        </p>
        <p className="flex items-center gap-2">
          <Badge tone="success">Certificate</Badge>
          issued automatically on first visit over HTTPS
        </p>
      </div>

      <p className="text-sm text-muted-foreground">
        Next: create your extensions, then add a SIP trunk so calls can reach the outside world.
      </p>

      <Button onClick={onComplete}>Go to the dashboard</Button>
    </Shell>
  );
}

// Not min-h-screen: this renders both standalone on first run and inside the app shell from the
// System menu, where a full viewport height would leave a screenful of empty space below the card.
function Shell({ step, children }: { step: number; children: React.ReactNode }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4">
      <Card className="w-full max-w-lg space-y-5">
        <Logo size={36} />
        <div className="flex gap-1.5">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              className={`h-1 flex-1 rounded-full ${n <= step ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
        {children}
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="w-32 px-4 py-2 text-muted-foreground">{label}</td>
      <td className="px-4 py-2 font-mono">{value}</td>
    </tr>
  );
}
